import { createTheme } from '@mui/material/styles';
import { hexToRGBA } from '../utils';
import type {} from '@mui/x-data-grid/themeAugmentation';
import { ReactComponent as ArrowDownCircle } from '../assets/icons/ArrowDownCircle.svg';
import { MuiPalette, Project_Specific_Palette } from '../style/colorTheme';

export const theme = createTheme({
  palette: MuiPalette,
  typography: {
    fontFamily: 'Poppins',
    h3: {
      //Home Page - Dashboard
      fontSize: '24px',
      fontWeight: 700,
      lineHeight: '12px',
    },
    h4: {
      //Menu - Header
      fontSize: '18px',
      fontWeight: 700,
      lineHeight: '100%',
    },
    h5: {
      //Layout - welcome text, Page title
      fontSize: '16px',
      fontWeight: 400,
    },
    h6: {
      //Dialog title, Menu Item selected
      fontSize: '14px',
      fontWeight: 700,
    },
    subtitle1: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 'unset',
    },
    button: {
      fontSize: '14px',
      fontWeight: 400,
      textTransform: 'capitalize',
    },
    body1: {
      //Data Grid item counts
      fontSize: '12px',
      fontWeight: 300,
    },
    body2: {
      //Data Grid Text
      fontSize: '10px',
      fontWeight: 400,
    },
  },
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          backgroundColor: theme.palette.background.default,
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          // outlined Textfield
          legend: { display: 'none' },
          fieldset: { top: 0 },
        },
      },
    },
    MuiFormControl: {
      defaultProps: { size: 'small' as const },
      styleOverrides: {
        root: ({ theme }) => ({
          //Phone code section search field
          verticalAlign: 'unset',
          //hide textfield and select shrink effect
          '& .MuiInputLabel-shrink': {
            display: 'none',
          },
          //phone code searchField svg
          '& .MuiSvgIcon-root': {
            fill: theme.palette.common.white,
          },
        }),
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          // MpTextField style
          backgroundColor: theme.palette.common.black,
          border: `1px solid ${theme.palette.divider}`,
        }),
      },
    },

    MuiSelect: {
      defaultProps: {
        IconComponent: ArrowDownCircle,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: '100px',
          '&.MuiInputBase-root': {
            //select field color
            backgroundColor: theme.palette.secondary.main,
            '> svg': {
              //style of select field default svg
              top: 'calc(50% - 6px)',
              width: '12px',
              height: '12px',
              right: '16px',
            },
          },
          '&.MuiOutlinedInput-root': {
            '& fieldset': {
              //remove select field inner border color
              borderColor: theme.palette.secondary.main,
            },
          },
        }),
      },
    },

    MuiList: {
      styleOverrides: {
        root: ({ theme }) => ({
          //Select Field Items behavior
          li: {
            ':hover': {
              backgroundColor: hexToRGBA(theme.palette.primary.main, 0.5),
            },
            '&.Mui-selected': {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.primary.main,
            },
          },
        }),
      },
    },

    //For calendar, Date Object
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiCalendarPicker-root': {
            svg: {
              // Calendar left right arrow
              fill: theme.palette.text.primary,
            },
          },
          '& .MuiClockPicker-root': {
            '& .MuiButtonBase-root': {
              // Calendar Am Pm button
              color: theme.palette.text.primary,
            },
          },
        }),
      },
    },

    //Mui Button
    MuiButton: {
      variants: [
        {
          //primary contained button
          props: { variant: 'contained', color: 'primary' },
          style: ({ theme }) => ({
            backgroundColor: Project_Specific_Palette.PrimaryButton,
            ':hover': {
              backgroundColor: hexToRGBA(Project_Specific_Palette.PrimaryButton, 0.6),
            },
          }),
        },
        {
          // specify style of outlined button
          props: { variant: 'outlined' },
          style: ({ theme }) => ({
            color: theme.palette.common.white,
            borderColor: theme.palette.divider,
            ':hover': {
              borderColor: theme.palette.common.white,
            },
          }),
        },
        {
          //specify style of success button
          props: { color: 'success' },
          style: ({ theme }) => ({
            backgroundColor: Project_Specific_Palette.Status.Success,
            color: theme.palette.common.black,
          }),
        },
      ],
    },

    MuiAlert: {
      styleOverrides: {
        root: ({ theme }) => ({
          // alert message style
          ...theme.typography.subtitle1,
          whiteSpace: 'pre-wrap',
          '& .MuiAlert-message': {
            padding: '9px 0',
          },
        }),
      },
    },

    //Mui Table
    MuiPaginationItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-selected': {
            // Table Pagaination item
            backgroundColor: hexToRGBA(theme.palette.secondary.main, 0.5),
          },
        }),
      },
    },

    MuiDataGrid: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiDataGrid-columnHeaders': {
            //Table Column style
            backgroundColor: theme.palette.background.paper,
            boxShadow: 'none',
            borderBottom: 'none',
            '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
              borderRight: `1px solid ${theme.palette.divider}`,
            },
          },
          //data grid scroll bar
          '*::-webkit-scrollbar': {
            height: '7px',
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: hexToRGBA(theme.palette.secondary.main, 0.5),
            borderRadius: '10px',
          },
          '& .MuiDataGrid-panel': {
            border: '1px solid red',
          },
          //remove whole DataGrid border
          border: 'none',
          '& .MuiDataGrid-main': {
            //Border of whole table, not include count and paginstion
            border: `1px solid ${theme.palette.divider}`,
          },

          '& .MuiDataGrid-row': {
            //Table row style
            backgroundColor: theme.palette.secondary.main,
            ':hover': {
              backgroundColor: hexToRGBA(theme.palette.secondary.main, 0.5),
            },
            '& .MuiDataGrid-cell': {
              borderTop: `0.5px solid ${theme.palette.divider}`,
              borderBottom: `0.5px solid ${theme.palette.divider}`,
            },
          },
          svg: {
            '&.MuiDataGrid-iconSeparator': {
              display: 'none',
            },
            //Table Colomns icon
            fill: theme.palette.text.primary,
          },
        }),
      },
    },
  },
});
